import React, { useState, useEffect, FC } from 'react';
import { Form, Checkbox, DatePicker, Select, Tag, Row, Col, Tooltip, CheckboxChangeEvent } from 'antd';
import dayjs from 'dayjs';
import { FormInstance } from 'antd/lib/form';

import { QuestionCircleOutlined } from '@ant-design/icons';

import { useDispatch, useSelector } from 'react-redux';

import * as actions from '../../store/actions';

import { RootState } from '../../../../store';

import './../../styles.less';

interface ReminderProps {
  form: FormInstance;
  isReminderApplied: boolean;
  changeReminder: (checked: boolean) => void;
}

interface TagRenderProps {
  label: React.ReactNode;
  value: string;
  closable: boolean;
  onClose: () => void;
}

const ReminderComponent: FC<ReminderProps> = ({ form, isReminderApplied, changeReminder }) => {
  const [isSendEmailApplied, setSendEmail] = useState<boolean>(false);

  const { reminderEmails, reminderEmailsLoading } = useSelector((state: RootState) => state.noteTypes);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isReminderApplied) {
      setSendEmail(false);

      form.resetFields();
    } else {
      dispatch(actions.getReminderEmails() as any);

      form.setFieldsValue({
        addReminder: isReminderApplied,
      });
    }
  }, [isReminderApplied, form, dispatch]);

  useEffect(() => {
    if (reminderEmails && reminderEmails.length) {
      for (let i = 0; i < reminderEmails.length; i++) {
        if (reminderEmails[i].selected) {
          form.setFieldsValue({
            reminderSendToUsers: [`${reminderEmails[i].userId}|${reminderEmails[i].email}|${reminderEmails[i].name}`],
          });
        }
      }
    }
  }, [reminderEmails, form]);

  const handleSelectDateTime = (time: dayjs.Dayjs): void => {
    if (time) {
      form.setFieldsValue({
        reminderDateTime: time.utc(true),
      });
    }
  };

  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const tagRender = (props: TagRenderProps): React.ReactElement => {
    const { label, value, closable, onClose } = props;
    const color = /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/.test(value) ? 'default' : 'error';
    return (
      <Tag color={color} closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {label}
      </Tag>
    );
  };

  const handleCustomEmailChange = (items: string[]): void => {
    form.setFieldsValue({
      reminderSendToCustomEMail: items && items.length ? items : undefined,
    });
  };

  const handleSendEmailsChange = (checked: boolean): void => {
    setSendEmail(checked);

    form.setFieldsValue({
      sendEmails: checked,
    });
  };

  return (
    <Form form={form} {...formItemLayout} style={{ width: '100%' }} layout="horizontal" size="small" labelAlign="left">
      <Form.Item name={['addReminder']} valuePropName="checked" initialValue={false} wrapperCol={{ span: 24 }}>
        <Checkbox className="nc-notes-checkbox" onChange={(e: CheckboxChangeEvent) => changeReminder(e.target.checked)}>
          Reminder
        </Checkbox>
      </Form.Item>

      {isReminderApplied ? (
        <>
          <Form.Item
            label="Date and time"
            name={['reminderDateTime']}
            initialValue={dayjs().add(1, 'day').set('hour', 8).set('minute', 0).set('second', 0).set('millisecond', 0).utc(true)}
            rules={[{ required: true, message: 'This field is required.' }]}
          >
            <DatePicker
              use12Hours
              minuteStep={5}
              needConfirm={false}
              disabledDate={(current): boolean => current && current < dayjs().subtract(1, 'day').endOf('day')}
              placeholder="Select date and time"
              showTime={{ format: 'hh:mm A' }}
              format="MMM DD, YYYY hh:mm A"
              style={{ width: '100%' }}
              onChange={handleSelectDateTime}
            />
          </Form.Item>

          <Form.Item label="User" name={['reminderSendToUsers']}>
            <Select
              mode="multiple"
              allowClear
              loading={reminderEmailsLoading}
              placeholder="Please select users"
              maxTagCount={1}
            >
              {reminderEmails && reminderEmails.length
                ? reminderEmails.map((email) => (
                  <Select.Option value={`${email.userId}|${email.email}|${email.name}`} key={email.userId}>
                    {email.name} ({email.email})
                  </Select.Option>
                ))
                : null}
            </Select>
          </Form.Item>

          <Form.Item
            name={['reminderSendToCustomEMail']}
            rules={[
              {
                pattern: /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/,
                message: 'Please enter valid email addresses',
              },
            ]}
            label={
              <Form.Item noStyle name={['sendEmails']} valuePropName="checked">
                <div>
                  <Checkbox className="nc-notes-checkbox" checked={isSendEmailApplied} onChange={(e) => handleSendEmailsChange(e.target.checked)}>
                    Custom emails
                  </Checkbox>
                  <Tooltip
                    title="If checked, recipients will receive a reminder to the custom email addresses you specify."
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
              </Form.Item>
            }
          >
            {isSendEmailApplied ? (
              <Select
                allowClear
                mode="tags"
                tagRender={tagRender}
                placeholder="Please enter custom emails"
                onChange={handleCustomEmailChange}
              ></Select>
            ) : (
              <div></div>
            )}
          </Form.Item>

          <Row gutter={8}>
            <Col span={11}>
              <Form.Item name={['reminderAddToAgencyCalendar']} valuePropName="checked" wrapperCol={{ span: 24 }}>
                <Checkbox className="nc-notes-checkbox">Add To Agency Calendar</Checkbox>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name={['remindWithPopUp']} valuePropName="checked" wrapperCol={{ span: 24 }}>
                <Checkbox className="nc-notes-checkbox">Pop-up</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </>
      ) : null}
    </Form>
  );
};

export default ReminderComponent;